import copy from "copy-to-clipboard";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { useIntl } from "../../../../shared/core/i18n/use-intl";
import { accountBankDetailsService, accountManager, featuresManager } from "../../../../shared/core/service/services";
import { getSettlementBalance } from "../../../../shared/domains/account/account";
import { useAccounts } from "../../../../shared/domains/account/use-accounts";
import { isDefined } from "../../../../shared/utils/assert";
import { useObservable } from "../../../../shared/utils/observable";
import { Path } from "../../../core/routing/path";
import { ActionRow, ActionRowList } from "../../common/action-row";
import { AmountText } from "../../common/amount-text";
import { PageHeaderBand, PageHeaderSubtitle, PageHeaderTitle } from "../../common/page/page-header-band";
import { StaticAssets } from "../../styles/custom-assets";
import { shadows, theme } from "../../styles/theme";
import { UIConstants } from "../../styles/uiConstants";
import { useSelectedAccount } from "../account-transactions/utils/use-selected-account";
import { AccountFieldRow } from "./components/account-field-row";

export default function MyAccountScreen() {
  const accounts = useAccounts();
  const [account] = useSelectedAccount(accounts);
  const features = useObservable(featuresManager.features);
  const { formatMessage } = useIntl();
  const { push } = useHistory();

  const [accountName, setAccountName] = useState<string>(account?.name ?? "");
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  if (!account) {
    return null;
  }

  const downloadBankDetails = async () => {
    try {
      await accountBankDetailsService.downloadAccountBankDetails(account.id);
    } catch (e) {
      setError(e?.response?.data?.error?.message ?? String(e));
    }
  };

  /** Hands the details to the platform's share sheet, falling back to the clipboard without one. */
  const shareBankDetails = async () => {
    const details = [accountName, account.iban, account.bic].filter(Boolean).join("\n");
    if (navigator.share) {
      await navigator.share({ text: details }).catch(() => copy(details));
    } else {
      copy(details);
    }
  };

  const submitAccountName = () => {
    if (accountName === account.name) {
      return;
    }
    accountManager
      .updateAccountName(account.id, accountName)
      .then(() => setSuccess(formatMessage("accountDetails.success")))
      .catch((e) => setError(e?.response?.data?.message ?? String(e)));
  };

  return (
    <>
      <PageHeaderBand>
        <PageHeaderTitle>{formatMessage("myAccountScreen.title")}</PageHeaderTitle>
        <PageHeaderSubtitle>{formatMessage("myAccountScreen.subtitle")}</PageHeaderSubtitle>
      </PageHeaderBand>
      <Columns>
        <MainColumn>
          <Card>
            <CardTitle>{formatMessage("myAccountScreen.coordinatesTitle")}</CardTitle>
            <AccountFieldRow
              label={formatMessage("accountDetails.accountName")}
              value={accountName}
              onChange={
                features.sdaAccountsNameUpdate
                  ? (name) => {
                      setSuccess("");
                      setError("");
                      setAccountName(name);
                    }
                  : undefined
              }
              onSubmit={submitAccountName}
            />
            {!!error && <FieldError>{error}</FieldError>}
            {!!success && <FieldSuccess>{success}</FieldSuccess>}
            <AccountFieldRow label={formatMessage("accountDetails.accountIdTitle")} value={account.id} />
            {isDefined(account.iban) && (
              <AccountFieldRow label={formatMessage("accountDetails.ibanTitle")} value={account.iban} />
            )}
            {isDefined(account.bic) && (
              <AccountFieldRow label={formatMessage("accountDetails.bicTitle")} value={account.bic} />
            )}
          </Card>
        </MainColumn>
        <SideColumn>
          <BalanceCard>
            <BalanceRow>
              <BalanceTitle>{formatMessage("accountDetails.balanceTitle")}</BalanceTitle>
              <Balance amount={getSettlementBalance(account)} />
            </BalanceRow>
            <BalanceMessage>{formatMessage("accountDetails.balanceMessage")}</BalanceMessage>
          </BalanceCard>

          <SectionTitle>{formatMessage("myAccountScreen.quickActionsTitle")}</SectionTitle>
          <ActionRowList>
            <ActionRow
              label={formatMessage("myAccountScreen.downloadRib")}
              trailingIconSrc={StaticAssets.download}
              onClick={downloadBankDetails}
            />
            <ActionRow
              label={formatMessage("myAccountScreen.shareDetails")}
              trailingIconSrc={StaticAssets.share}
              onClick={shareBankDetails}
            />
            {features.beneficiariesManagement && (
              <ActionRow
                label={formatMessage("myAccountScreen.manageBeneficiaries")}
                subtitle={formatMessage("myAccountScreen.manageBeneficiariesSubtitle")}
                onClick={() => push(Path.Recipients)}
              />
            )}
            <ActionRow
              label={formatMessage("myAccountScreen.accountStatement")}
              subtitle={formatMessage("myAccountScreen.accountStatementSubtitle")}
              onClick={() => push(Path.AccountStatements)}
            />
            <ActionRow
              label={formatMessage("myAccountScreen.feesStatement")}
              subtitle={formatMessage("myAccountScreen.feesStatementSubtitle")}
              onClick={() => push(Path.FeesStatements)}
            />
          </ActionRowList>
        </SideColumn>
      </Columns>
    </>
  );
}

const Columns = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
  width: 100%;

  @media (max-width: ${UIConstants.DESKTOP_SMALL_BREAKPOINT - 1}px) {
    flex-direction: column;
  }
`;

const MainColumn = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
`;

const SideColumn = styled.div`
  display: flex;
  flex: 0 0 380px;
  flex-direction: column;

  @media (max-width: ${UIConstants.DESKTOP_SMALL_BREAKPOINT - 1}px) {
    flex: 1 1 auto;
    align-self: stretch;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  border: 1px solid ${theme.colors.gray[100]};
  border-radius: 12px;
  background-color: #ffffff;
  ${shadows.sm};
`;

const CardTitle = styled.h2`
  ${theme.mediumText};
  margin: 0 0 8px;
  font-size: 20px;
  line-height: 26px;
  color: ${theme.colors.blue[800]};
`;

const BalanceCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 24px;
  border: 1px solid ${theme.colors.blue[200]};
  border-radius: 12px;
  background-color: ${theme.colors.blue[100]};
  ${shadows.sm};
`;

const BalanceRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: space-between;
`;

const BalanceTitle = styled.span`
  ${theme.mediumText};
  font-size: 20px;
  line-height: 26px;
  color: ${theme.colors.blue[800]};
`;

const Balance = styled(AmountText)`
  ${theme.boldText};
  font-size: 18px;
  color: ${theme.colors.blue[800]};
`;

const BalanceMessage = styled.span`
  ${theme.text};
  margin-top: 6px;
  max-width: 260px;
  font-size: 12px;
  line-height: 16px;
  color: ${theme.colors.gray[800]};
`;

const FieldError = styled.span`
  ${theme.text};
  font-size: 12px;
  color: ${theme.colors.error[500]};
`;

const FieldSuccess = styled.span`
  ${theme.text};
  font-size: 12px;
  color: ${theme.colors.success[500]};
`;

const SectionTitle = styled.h2`
  ${theme.mediumText};
  margin: 24px 0 12px;
  font-size: 14px;
  line-height: 18px;
  color: ${theme.colors.gray[800]};
`;
